import * as React from 'react';
import type { ServiceModule } from '../../api/content';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

type Props = {
  modules: ServiceModule[];
  /** Body-appended host so modals escape drei's <Scroll html> transform. */
  portalHost?: HTMLElement | null;
  /** Service CTA (CMS `ctaLabel`); with `onCta` it renders as the modal footer. */
  ctaLabel?: string;
  /** Opens the service booking flow; the module modal closes first. */
  onCta?: () => void;
};

/** Generic CMS-driven module buttons for a service card.
 *  Each module renders an outline trigger that opens a titled modal: a flat
 *  bullet list by default, or a numbered step breakdown when `steps` is set. */
export function ServiceModules({ modules, portalHost, ctaLabel, onCta }: Props) {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const open = openIndex !== null ? modules[openIndex] : null;
  const steps = open?.steps ?? [];

  if (modules.length === 0) return null;

  return (
    <div className="mt-8 flex flex-wrap gap-3 max-w-2xl">
      {modules.map((mod, i) => (
        <Button
          key={mod.buttonLabel}
          variant="outline"
          surface="dark"
          onClick={() => setOpenIndex(i)}
        >
          {mod.buttonLabel}
        </Button>
      ))}

      {open ? (
        <Modal
          open
          onClose={() => setOpenIndex(null)}
          eyebrow={open.eyebrow || undefined}
          title={open.title}
          size={steps.length > 0 ? "wide" : "default"}
          container={portalHost ?? null}
          footerLayout="full"
          footer={
            ctaLabel && onCta ? (
              <div className="flex w-full flex-col [&>button]:w-full">
                <Button
                  variant="solid"
                  surface="light"
                  as="button"
                  type="button"
                  onClick={() => {
                    setOpenIndex(null);
                    onCta();
                  }}
                >
                  {ctaLabel}
                </Button>
              </div>
            ) : undefined
          }
        >
          {steps.length > 0 ? (
            <ol className="flex flex-col gap-7">
              {steps.map((step, i) => (
                <li key={step.title} className="flex gap-4">
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 flex-none items-center justify-center border border-primary/40 text-sm font-medium text-primary"
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium uppercase tracking-eyebrow text-primary">
                      {step.title}
                    </h3>
                    {step.description ? (
                      <p className="mt-2 text-sm leading-snug text-primary/80">
                        {step.description}
                      </p>
                    ) : null}
                    {step.items.length > 0 ? (
                      <ul className="t-deck-bullets t-deck-bullets--diamond mt-3">
                        {step.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <ul className="t-deck-bullets t-deck-bullets--diamond">
              {open.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </Modal>
      ) : null}
    </div>
  );
}
